import { useAuth } from "@clerk/clerk-react";
import {
  Box,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { FormEvent, useContext, useState } from "react";

import StyledLargeButton from "components/Styled/Button/StyledLargeButton";
import StyledDialog from "components/Styled/Dialog";
import StyledLink from "components/Styled/Link";
import StyledTextField from "components/Styled/TextField";
import { fetcherAuth } from "utils/api";

import { BRAND_AMBASSADOR_TERMS_AND_CONDITIONS } from ".";

const URL_REGEX = /^https?:\/\/.+\..+/;

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function SubmitContentDialog({ open, onClose }: Props) {
  const { getToken } = useAuth();
  const { currentOrg } = useContext(OrganizationUserContext);
  const { setErrorAlert, setAlert } = useContext(AlertContext);

  const [sparkCode, setSparkCode] = useState("");
  const [tiktokUrl, setTiktokUrl] = useState("");
  const [urlError, setUrlError] = useState("");
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleUrlBlur = () => {
    if (tiktokUrl.trim() && !URL_REGEX.test(`https://tiktok.com${tiktokUrl}`)) {
      setUrlError("Please enter a valid TikTok URL");
    } else {
      setUrlError("");
    }
  };

  const handleClose = () => {
    setSparkCode("");
    setTiktokUrl("");
    setUrlError("");
    setAgreedToTerms(false);
    onClose();
  };

  const isValid =
    sparkCode.trim() &&
    tiktokUrl.trim() &&
    URL_REGEX.test(`https://tiktok.com${tiktokUrl}`) &&
    agreedToTerms;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!isValid) return;
    setLoading(true);
    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/brand-ambassador`,
        "POST",
        {},
        { sparkCode, tiktokUrl: `https://tiktok.com${tiktokUrl}` },
      );
      setAlert("Content submitted successfully!", "success");
      handleClose();
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <StyledDialog
      title="Submit your content"
      open={open}
      handleClose={handleClose}
      maxWidth="sm"
      actionContainer={
        <>
          <StyledLargeButton color="secondary" onClick={handleClose}>
            Cancel
          </StyledLargeButton>
          <StyledLargeButton
            disabled={!isValid}
            onClick={handleSubmit}
            loading={loading}
          >
            Submit
          </StyledLargeButton>
        </>
      }
    >
      <Box component="form" onSubmit={handleSubmit} sx={{ pt: 1 }}>
        <StyledTextField
          label="TikTok Spark Code"
          placeholder="#s63DdePaj9HjSv2BPcSrDfTxU2X506b2o..."
          fullWidth
          value={sparkCode}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setSparkCode(e.target.value)
          }
          styles={{ mb: 3 }}
        />

        <StyledTextField
          label="TikTok Video URL"
          placeholder="@username/video/..."
          fullWidth
          value={tiktokUrl}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setTiktokUrl(e.target.value);
            if (urlError) setUrlError("");
          }}
          onBlur={handleUrlBlur}
          error={!!urlError}
          helperText={urlError}
          styles={{ mb: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Typography sx={{ color: grey[800] }}>
                  https://tiktok.com
                </Typography>
              </InputAdornment>
            ),
          }}
        />

        <FormControlLabel
          sx={{ mt: 2, alignItems: "flex-start" }}
          control={
            <Checkbox
              checked={agreedToTerms}
              onChange={(e) => setAgreedToTerms(e.target.checked)}
              size="small"
              sx={{ pt: 0 }}
            />
          }
          label={
            <Typography variant="body2">
              I agree to the{" "}
              <StyledLink
                href={BRAND_AMBASSADOR_TERMS_AND_CONDITIONS}
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms &amp; Conditions
              </StyledLink>
            </Typography>
          }
        />
      </Box>
    </StyledDialog>
  );
}
